import Card from '../../../components/card/Card';
import P from '../../../components/texts/p/P';
import DropDown from '../../../components/dropdown/DropDown';
import Input from '../../../components/input/Input';
import Button from '../../../components/button/Button';
import { useClientTheme } from '../../../context/ThemeContext';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { MONTH_NAMES } from '../../../utils/Constants';
import { resolveLocalCurrencyCode } from '../../../utils/quotationAssets';
import {
  postBalanceSheetData,
  BalancePeriod,
  PostBalanceLocalItem,
  SalesPurchasesByMonth,
} from '../../../types/interfaces';
import { formatCurrency } from '../../../utils/Masks';
import Span from '../../../components/texts/span/Span';
import H from '../../../components/texts/h/H';
import Icon from '../../../components/icon/Icon';
import { NumericFormat } from 'react-number-format';
import { toNumber } from '../../../utils/Utils';
import { useConfig } from '../../../context/ConfigContext';

interface MonthOption {
  id: number;
  label: string;
}

interface PostBalanceSheetProps {
  onChangeSheet: (payload: { values: postBalanceSheetData[]; isValid: boolean }) => void;
  subtitle: string;
  totalText: string;
  validateForms: boolean | null;
  cloudData: SalesPurchasesByMonth;
  localData: PostBalanceLocalItem[] | undefined;
  disableFields: boolean;
  showAllErrors?: boolean;
  balanceLastMonth: BalancePeriod | null | undefined;
  inputMarginTop?: string;
}

const PostBalanceSheet: React.FC<PostBalanceSheetProps> = ({
  onChangeSheet,
  subtitle,
  totalText,
  validateForms,
  cloudData,
  localData,
  disableFields,
  showAllErrors = false,
  balanceLastMonth,
  inputMarginTop = '0px'
}) => {
  const appConfig = useConfig();
  const isUruguayEnabled = appConfig?.isUruguayEnabled === true;
  const localCurrencyCode = resolveLocalCurrencyCode(appConfig);
  const { clientTheme } = useClientTheme();
  const [disableAddSheetMonth, setDisableAddSheetMonth] =
    useState<boolean>(true);
  const [months, setMonths] = useState<MonthOption[]>([]);
  const currentMonthIndex =
    new Date().getFullYear() * 12 + new Date().getMonth();
  const [totalAmounts, setTotalAmounts] = useState<number>(0);
  const [, setShouldValidateForm] = useState<boolean>(false);
  const currentDate = new Date();

  const validateSheetData = (values: postBalanceSheetData[]) => {
    const errors: Record<number, { month?: string; amount?: string }> = {};
    values.forEach((product, index) => {
      const sheetErrors: { month?: string; amount?: string } = {};
      if (
        product.amount?.value === '' ||
        product.amount?.value === null ||
        product.amount?.value === undefined
      ) {
        sheetErrors.amount = "Debés completar el campo 'Monto'";
      }
      if (product.monthId === null || product.monthId === undefined) {
        sheetErrors.month = 'Debés seleccionar un mes';
      }
      errors[index] = sheetErrors;
    });
    return errors;
  };

  const sheetFormik = useFormik({
    initialValues: [
      {
        id: 1,
        monthId: null,
        month: null,
        year: null,
        amount: {
          currency_code: localCurrencyCode,
          value: 0,
        },
      },
    ] as postBalanceSheetData[],
    validate: validateSheetData,
    onSubmit: () => {},
  });

  useEffect(() => {
    const toYear = Number(balanceLastMonth?.to_year) || currentDate.getFullYear();
    const toMonth = Number(balanceLastMonth?.to_month) || 0;
    const startMonth = toMonth + 1;
    const startDate = new Date(toYear, startMonth - 1);
    const diffInMonths =
      (currentDate.getFullYear() - startDate.getFullYear()) * 12 +
      (currentDate.getMonth() - startDate.getMonth());

    if (typeof diffInMonths !== 'number' || diffInMonths < 0) {
      setMonths([]);
      return;
    }

    let monthsArray: MonthOption[] = [];
    for (let i = 0; i <= diffInMonths; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );
      const formattedMonth = `${MONTH_NAMES[date.getMonth()]} - ${date.getFullYear()}`;

      monthsArray.push({
        id: diffInMonths - i,
        label: formattedMonth,
      });
    }
    monthsArray = monthsArray.reverse();
    setMonths(monthsArray);

    if (localData && localData.length > 0) {
      const transformedData: postBalanceSheetData[] = localData.map((item, idx) => ({
        id: idx + 1,
        month: item.month,
        monthId: monthsArray.find(
          (m) => m.label === MONTH_NAMES[item.month - 1] + ' - ' + item.year
        )?.id ?? null,
        amount: {
          currency_code: localCurrencyCode,
          value:
            typeof item.amount === 'number'
              ? item.amount
              : toNumber(item.amount),
        },
        year: item.year,
      }));
      sheetFormik.setValues(transformedData);
    }
  }, [balanceLastMonth?.to_year, balanceLastMonth?.to_month, localData]);

  useEffect(() => {
    if (validateForms !== null) {
      setShouldValidateForm(true);
      sheetFormik.validateForm();
    }
  }, [validateForms]);

  useEffect(() => {
    if (!cloudData || typeof cloudData !== 'object') return;
    const entries = Object.entries(cloudData);
    const transformedValues: postBalanceSheetData[] = entries.map(([key, value], index) => ({
      id: index + 1,
      monthId: index,
      month: parseInt(key.slice(4, 6), 10),
      year: parseInt(key.slice(0, 4), 10),
        amount: {
          currency_code: localCurrencyCode,
          value: typeof value === 'number' ? value : (value != null ? toNumber(value) : 0),
        },
    }));
    if (transformedValues.length === 0) return;

    const monthsFromCloud = entries.map(([key], index) => ({
      id: index,
      label: `${MONTH_NAMES[parseInt(key.slice(4, 6), 10) - 1]} - ${key.slice(0, 4)}`,
    }));

    if (months.length === 0) {
      setMonths(monthsFromCloud);
      const toShow = Math.min(6, transformedValues.length);
      sheetFormik.setValues(
        transformedValues.slice(0, toShow).map((v, i) => ({
          ...v,
          monthId: i,
          id: i + 1,
        }))
      );
      return;
    }

    handleMonth(
      MONTH_NAMES[(transformedValues[0].month ?? 1) - 1] + ' - ' + transformedValues[0].year,
      transformedValues,
      transformedValues.length
    );
  }, [cloudData, months.length]);

  useEffect(() => {
    if (showAllErrors) {
      const touchedArr = sheetFormik.values.map(() => ({
        monthId: true,
        amount: { value: true },
      }));
      sheetFormik.setTouched(touchedArr as Parameters<typeof sheetFormik.setTouched>[0], false);
    }
  }, [showAllErrors, sheetFormik.values.length]);

  useEffect(() => {
    updateTotals();
    const lastMonthId = months.length > 0 ? months[months.length - 1].id : undefined;
    const lastValueMonthId = sheetFormik.values.length > 0 ? sheetFormik.values[sheetFormik.values.length - 1].monthId : undefined;
    lastValueMonthId === lastMonthId || sheetFormik.values[0]?.monthId === null
      ? setDisableAddSheetMonth(true)
      : setDisableAddSheetMonth(false);

    const isValid =
      sheetFormik.values.length > 0 &&
      sheetFormik.values.every((_, idx) => {
        const err = (sheetFormik.errors as Record<number, { month?: string; amount?: string }>)[idx] ?? {};
        return !err.month && !err.amount;
      });

    onChangeSheet({ values: sheetFormik.values, isValid });
  }, [sheetFormik.values, sheetFormik.errors, months]);

  const handleMonth = (
    event: string,
    data: any = null,
    monthsToDisplay: number = 6
  ) => {
    setShouldValidateForm(false);
    const found = months.find((item) => item.label === event);
    if (found == null) return;
    const selectedIndex = found.id;
    const allowedMonths = Math.min(
      monthsToDisplay,
      currentMonthIndex - selectedIndex + 1
    );
    const inputsToRender = months
      .slice(selectedIndex, selectedIndex + allowedMonths)
      .map((month, index) => ({
        id: month.id,
        monthId: month.id,
        month:
          MONTH_NAMES.findIndex(
            (newMonth) => newMonth === (month.label.split('-').shift()?.trim() ?? '')
          ) + 1,
        year: parseInt(month.label.split('-').pop()?.trim() ?? '0', 10),
        amount: {
          currency_code: localCurrencyCode,
          value: toNumber(data?.[index]?.amount?.value) ?? 0,
        },
      }));
    sheetFormik.setValues([...inputsToRender]);
    sheetFormik.isValid = false;
  };

  const updateTotals = () => {
    let totalTemp = 0;
    sheetFormik.values.forEach((product: any) => {
      totalTemp += toNumber(product.amount.value);
    });
    setTotalAmounts(totalTemp);
  };

  const addSheetMonth = () => {
    if (sheetFormik.values.length === 0) return;

    const sortedValues = [...sheetFormik.values].sort((a, b) =>
      (a.year ?? 0) === (b.year ?? 0) ? (a.month ?? 0) - (b.month ?? 0) : (a.year ?? 0) - (b.year ?? 0)
    );

    const lastAddedMonth = sortedValues[sortedValues.length - 1];
    if (!lastAddedMonth || lastAddedMonth.month == null || lastAddedMonth.year == null) return;

    let newMonth = (lastAddedMonth.month ?? 1) + 1;
    let newYear = lastAddedMonth.year ?? new Date().getFullYear();

    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    if (
      newYear > currentYear ||
      (newYear === currentYear && newMonth > currentMonth)
    ) {
      setDisableAddSheetMonth(true);
      return;
    }

    const existingIndex = sheetFormik.values.findIndex(
      (entry) => entry.month === newMonth && entry.year === newYear
    );

    const updatedValues = [...sheetFormik.values];

    if (existingIndex !== -1) {
      updatedValues[existingIndex].amount.value = lastAddedMonth.amount.value;
    } else {
      updatedValues.push({
        id: Math.max(...updatedValues.map((e) => e.id), 0) + 1,
        monthId: Math.max(...updatedValues.map((e) => (e.monthId != null ? Number(e.monthId) : 0)), 0) + 1,
        month: newMonth,
        year: newYear,
        amount: {
          currency_code: localCurrencyCode,
          value: 0,
        },
      });
    }
    sheetFormik.setValues(updatedValues);
  };

  const deleteSheetRow = (index: number) => {
    sheetFormik.setValues(sheetFormik.values.filter((_, i) => i !== index));
  };

  return (
    <Card spacing={{ left: isUruguayEnabled ? '0' : '6' }}>
      <Card display='flex'>
        <Card width='31%'>
          <P type='2' weight='bold' variant='neutral'>
            Mes
          </P>
        </Card>
        <Card>
          <P type='2' weight='bold' variant='neutral'>
            {subtitle}
          </P>
        </Card>
      </Card>
      {sheetFormik.values.map((row: postBalanceSheetData, index: number) => (
        <Card display='flex' gap='16px' margin={{ bottom: '3' }} key={row.id}>
          <Card width='30%'>
            <DropDown
              id='risk-month-dropdown'
              items={months}
              size='md'
              title={
                months.find(
                  (item) => item.id === sheetFormik.values[index].monthId
                )
                  ? months.find(
                      (item) => item.id === sheetFormik.values[index].monthId
                    )?.label
                  : 'Seleccioná un mes'
              }
              onChange={(e) => {
                sheetFormik.setFieldTouched(`[${index}].monthId`, true, false);
                if (e != null) handleMonth(e);
              }}
              disabled={index > 0 || disableFields}
            />
            <Span variant='danger' type='2'>
              {sheetFormik.touched?.[index]?.monthId &&
                sheetFormik.errors[index]?.month}
            </Span>
          </Card>
          <Card width='70%'>
            <Card display='flex' gap='16px'>
              <div style={{ width: '100%', marginTop: inputMarginTop }}>
                <NumericFormat
                customInput={Input}
                inputStatus={
                  sheetFormik.errors[index]?.amount ? 'error' : 'normal'
                }
                id='risk-input-value'
                maxLength={40}
                placeholder={subtitle}
                value={row.amount.value ?? ''}
                thousandSeparator='.'
                decimalSeparator=','
                  style={{ width: '100%' }}
                  onValueChange={(values) => {
                    sheetFormik.setFieldTouched(
                      `[${index}].amount.value`,
                      true,
                      false
                    );
                    sheetFormik.setFieldValue(
                      `[${index}].amount.value`,
                      values.value === '' ? '' : values.floatValue
                    );
                  }}
                  decimalScale={2}
                  disabled={disableFields}
                />
              </div>
              {index === sheetFormik.values.length - 1 && index !== 0 && (
                <Button
                  size='md'
                  variant='secondary'
                  onClick={() => deleteSheetRow(index)}
                  disabled={disableFields}
                >
                  <Card variant='none'>
                    <Icon
                      variant='neutral'
                      name='delete_outline'
                      cursor='pointer'
                    />
                  </Card>
                </Button>
              )}
            </Card>
            <Span variant='danger' type='2'>
              {sheetFormik.touched?.[index]?.amount?.value &&
                sheetFormik.errors[index]?.amount}
            </Span>
          </Card>
        </Card>
      ))}
      <Button
        id='application-add-button'
        variant='tertiary'
        size='md'
        onClick={addSheetMonth}
        disabled={disableAddSheetMonth || disableFields}
      >
        + Agregar mes
      </Button>
      <Card
        border={true}
        display='flex'
        variant={clientTheme ? 'primary' : 'success'}
        modifier='weak'
        spacing={'3'}
        margin={{ top: '3' }}
        justifyContent='center'
        alignItems='center'
      >
        <Card display='flex' gap='24px'>
          <H type='3' withMargin={false} variant='neutral'>
            {totalText}:
          </H>
          <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
            {localCurrencyCode} {formatCurrency(totalAmounts ?? 0, true)}
          </H>
        </Card>
      </Card>
    </Card>
  );
};

export default PostBalanceSheet;
